import React, { useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Button, Table, Form, Spinner, Card } from "react-bootstrap";
import Select from "react-select";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { TIMEZONE } from "../constans";
import { pickReportManagersFromSchedule, summaryDirectNames } from "../helpers/directManagerDialogConversion";

dayjs.extend(utc);
dayjs.extend(timezone);

function defaultLeadsEditorDate() {
    return dayjs().tz(TIMEZONE).subtract(1, "day").startOf("day").toDate();
}

/** Позавчора за календарем Києва (як у мобільному застосунку). */
function dayBeforeYesterdayKyivDate() {
    return dayjs().tz(TIMEZONE).subtract(2, "day").startOf("day").toDate();
}

/** Відображення: DD-MM-YYYY. Терпимо до рядка YYYY-MM-DD, ISO та порожніх значень. */
function formatDateForTable(raw) {
    if (raw == null || raw === "") return "—";
    const s = String(raw).trim();
    let d = dayjs(s);
    if (!d.isValid() && /^\d{4}-\d{2}-\d{2}/.test(s)) {
        d = dayjs(s.slice(0, 10));
    }
    return d.isValid() ? d.format("DD-MM-YYYY") : s || "—";
}

function DateQuickButtons({ className = "", onDayBeforeYesterday, onYesterday }) {
    return (
        <div
            className={`d-flex flex-wrap gap-1 leads-editor__quick-dates ${className}`}
            role="group"
            aria-label="Швидкий вибір дати"
        >
            <Button type="button" size="sm" variant="outline-secondary" className="flex-grow-1" onClick={onDayBeforeYesterday}>
                Позавчора
            </Button>
            <Button type="button" size="sm" variant="outline-secondary" className="flex-grow-1" onClick={onYesterday}>
                Вчора
            </Button>
        </div>
    );
}

export default function LeadsTicketsEditor() {
    const [date, setDate] = useState(() => defaultLeadsEditorDate());
    const [formData, setFormData] = useState({
        leadsTelegram: "",
        leadsInstagram: "",
        ticketsTelegram: "",
        ticketsInstagram: "",
        directManager: "",
        directManager2: "",
    });
    const [dataList, setDataList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [listRefreshing, setListRefreshing] = useState(false);
    /** Менеджери Директу — група «Дірект» у графіку змін; у БД (Summary.directManager) зберігається саме ПІБ. */
    const [directManagerNames, setDirectManagerNames] = useState([]);
    const [directManagersError, setDirectManagersError] = useState(false);

    const loadRows = async () => {
        const res = await fetch("/api/leads-tickets", { credentials: "include" });
        if (!res.ok) throw new Error("load failed");
        const data = await res.json();
        setDataList(Array.isArray(data) ? data : []);
    };

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                setLoadError(null);
                await loadRows();
            } catch {
                if (!cancelled) {
                    setLoadError("Не вдалося завантажити дані. Оновіть сторінку.");
                    setDataList([]);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const ym = dayjs().tz(TIMEZONE).format("YYYY-MM");
                const res = await fetch(`/api/schedule/month?ym=${encodeURIComponent(ym)}`, {
                    credentials: "include",
                });
                if (!res.ok) throw new Error("schedule failed");
                const data = await res.json();
                if (cancelled) return;
                const { direct } = pickReportManagersFromSchedule(data.participants);
                setDirectManagerNames(direct.map((m) => m.label));
                setDirectManagersError(false);
            } catch {
                if (!cancelled) setDirectManagersError(true);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    /** «Не обрано» + актуальні менеджери; збережене значення, якого вже немає в графіку (звільнені), лишається вибраним. */
    const managerSelectOptions = useMemo(() => {
        const names = [...directManagerNames];
        for (const v of [formData.directManager, formData.directManager2]) {
            const current = String(v || "").trim();
            if (current && !names.includes(current)) names.push(current);
        }
        return [
            { value: "", label: "Не обрано" },
            ...names.map((name) => ({ value: name, label: name })),
        ];
    }, [directManagerNames, formData.directManager, formData.directManager2]);

    const sortedList = useMemo(
        () => [...dataList].sort((a, b) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf()),
        [dataList]
    );

    const handleChange = (field, value) => {
        setFormData({ ...formData, [field]: value });
    };

    const setDayBeforeYesterday = () => setDate(dayBeforeYesterdayKyivDate());
    const setYesterday = () => setDate(defaultLeadsEditorDate());

    const handleSave = async () => {
        const formattedDate = dayjs(date).format("YYYY-MM-DD");

        const payload = {
            date: formattedDate,
            leads: {
                telegram: parseInt(formData.leadsTelegram || 0, 10),
                instagram: parseInt(formData.leadsInstagram || 0, 10),
            },
            tickets: {
                telegram: parseInt(formData.ticketsTelegram || 0, 10),
                instagram: parseInt(formData.ticketsInstagram || 0, 10),
            },
            directManager: formData.directManager || formData.directManager2 || "",
            directManager2: formData.directManager ? formData.directManager2 || "" : "",
        };

        const res = await fetch("/api/leads-tickets", {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify(payload),
        });

        if (res.ok) {
            try {
                setListRefreshing(true);
                await loadRows();
            } catch {
                /* ignore */
            } finally {
                setListRefreshing(false);
            }
            setFormData({
                leadsTelegram: "",
                leadsInstagram: "",
                ticketsTelegram: "",
                ticketsInstagram: "",
                directManager: "",
                directManager2: "",
            });
        }
    };

    const handleEdit = (entry) => {
        setDate(dayjs(entry.date).toDate());
        setFormData({
            leadsTelegram: entry.leads?.telegram ?? "",
            leadsInstagram: entry.leads?.instagram ?? "",
            ticketsTelegram: entry.tickets?.telegram ?? "",
            ticketsInstagram: entry.tickets?.instagram ?? "",
            directManager: entry.directManager || "",
            directManager2: entry.directManager2 || "",
        });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const controlClassMobile = "form-control form-control-sm w-100";
    const controlClassDesktop = "form-control w-100";

    const selectedManagerNames = [formData.directManager, formData.directManager2].filter(Boolean);

    /** До двох менеджерів Директу за день — діалоги в звітах діляться між ними порівну. */
    const renderManagerSelect = (compact = true) => (
        <Select
            isMulti
            isClearable={false}
            placeholder="Не обрано"
            noOptionsMessage={() => "Немає менеджерів"}
            options={managerSelectOptions.filter((o) => o.value)}
            value={managerSelectOptions.filter((o) => o.value && selectedManagerNames.includes(o.value))}
            isOptionDisabled={() => selectedManagerNames.length >= 2}
            onChange={(opts) => {
                const [first = "", second = ""] = (opts || []).map((o) => o.value);
                setFormData({ ...formData, directManager: first, directManager2: second });
            }}
            aria-label="Менеджери Директ"
            menuPortalTarget={document.body}
            styles={{
                menuPortal: (base) => ({ ...base, zIndex: 1060 }),
                control: (base) => ({
                    ...base,
                    minHeight: compact ? 31 : 38,
                    borderColor: directManagersError ? "#dc3545" : base.borderColor,
                }),
            }}
        />
    );

    return (
        <div className="leads-editor-page container mt-3 mt-md-4 px-3 px-md-4">
            <div className="leads-editor__head">
                <h4 className="leads-editor__title">Редактор лідів і тікетів</h4>
            </div>

            {loadError ? (
                <div className="alert alert-danger" role="alert">
                    {loadError}
                </div>
            ) : null}

            {loading ? (
                <div className="leads-editor__loading">
                    <Spinner animation="border" role="status" />
                    <span>Завантажуємо дані…</span>
                </div>
            ) : loadError ? null : (
                <>
                    {/* Мобільна версія: форма + картки історії */}
                    <Card className="leads-editor__mobile-form d-md-none mb-3">
                        <Card.Body className="leads-editor__mobile-form-body">
                            <div className="leads-editor__date-block mb-3">
                                <span className="leads-editor__section-label">Дата запису</span>
                                <DateQuickButtons onDayBeforeYesterday={setDayBeforeYesterday} onYesterday={setYesterday} />
                            </div>
                            <Form.Group className="mb-3">
                                <Form.Label className="leads-editor__section-label">Календар</Form.Label>
                                <DatePicker
                                    selected={date}
                                    onChange={setDate}
                                    dateFormat="dd-MM-yyyy"
                                    className={controlClassMobile}
                                    wrapperClassName="w-100"
                                    withPortal
                                    portalId="datepicker-portal-root"
                                />
                            </Form.Group>
                            <div className="leads-editor__metrics-grid row g-3 mb-1">
                                <div className="col-6">
                                    <Form.Group>
                                        <Form.Label className="leads-editor__section-label">Ліди Instagram</Form.Label>
                                        <Form.Control
                                            size="sm"
                                            type="number"
                                            inputMode="numeric"
                                            value={formData.leadsInstagram}
                                            onChange={(e) => handleChange("leadsInstagram", e.target.value)}
                                        />
                                    </Form.Group>
                                </div>
                                <div className="col-6">
                                    <Form.Group>
                                        <Form.Label className="leads-editor__section-label">Тікети Instagram</Form.Label>
                                        <Form.Control
                                            size="sm"
                                            type="number"
                                            inputMode="numeric"
                                            value={formData.ticketsInstagram}
                                            onChange={(e) => handleChange("ticketsInstagram", e.target.value)}
                                        />
                                    </Form.Group>
                                </div>
                                <div className="col-6">
                                    <Form.Group>
                                        <Form.Label className="leads-editor__section-label">Ліди Telegram</Form.Label>
                                        <Form.Control
                                            size="sm"
                                            type="number"
                                            inputMode="numeric"
                                            value={formData.leadsTelegram}
                                            onChange={(e) => handleChange("leadsTelegram", e.target.value)}
                                        />
                                    </Form.Group>
                                </div>
                                <div className="col-6">
                                    <Form.Group>
                                        <Form.Label className="leads-editor__section-label">Тікети Telegram</Form.Label>
                                        <Form.Control
                                            size="sm"
                                            type="number"
                                            inputMode="numeric"
                                            value={formData.ticketsTelegram}
                                            onChange={(e) => handleChange("ticketsTelegram", e.target.value)}
                                        />
                                    </Form.Group>
                                </div>
                            </div>
                            <Form.Group className="leads-editor__manager-block mb-0">
                                <Form.Label className="leads-editor__section-label">Менеджер (Директ)</Form.Label>
                                {renderManagerSelect()}
                            </Form.Group>
                            <Button variant="primary" className="leads-editor__save-btn w-100" onClick={handleSave}>
                                Зберегти
                            </Button>
                        </Card.Body>
                    </Card>

                    <div className="leads-editor__mobile-history d-md-none">
                        <div className="leads-editor__mobile-history-title-row">
                            <span className="leads-editor__mobile-history-title">Попередні дні</span>
                            {listRefreshing ? (
                                <Spinner animation="border" size="sm" className="leads-editor__history-spinner" role="status" />
                            ) : null}
                        </div>
                        {sortedList.length === 0 ? (
                            <p className="leads-editor__empty-hint text-muted small mb-0">Поки немає збережених записів.</p>
                        ) : (
                            <div className="leads-editor__mobile-history-list">
                            {sortedList.map((entry, idx) => (
                                <Card
                                    key={
                                        entry.date != null
                                            ? String(entry.date)
                                            : String(entry._id ?? `row-${idx}`)
                                    }
                                    className="leads-editor__mobile-card"
                                >
                                    <Card.Body>
                                        <div className="leads-editor__mobile-card-date">{formatDateForTable(entry.date)}</div>
                                        <div className="leads-editor__metric">
                                            <span className="leads-editor__metric-label">Ліди Instagram</span>
                                            <span className="leads-editor__metric-val">{entry.leads?.instagram ?? 0}</span>
                                        </div>
                                        <div className="leads-editor__metric">
                                            <span className="leads-editor__metric-label">Тікети Instagram</span>
                                            <span className="leads-editor__metric-val">{entry.tickets?.instagram ?? 0}</span>
                                        </div>
                                        <div className="leads-editor__metric">
                                            <span className="leads-editor__metric-label">Ліди Telegram</span>
                                            <span className="leads-editor__metric-val">{entry.leads?.telegram ?? 0}</span>
                                        </div>
                                        <div className="leads-editor__metric">
                                            <span className="leads-editor__metric-label">Тікети Telegram</span>
                                            <span className="leads-editor__metric-val">{entry.tickets?.telegram ?? 0}</span>
                                        </div>
                                        <div className="leads-editor__metric">
                                            <span className="leads-editor__metric-label">Менеджер</span>
                                            <span className="leads-editor__metric-val">{summaryDirectNames(entry).join(" + ") || "Не обрано"}</span>
                                        </div>
                                        <Button
                                            variant="warning"
                                            className="leads-editor__edit-btn w-100 mt-2"
                                            onClick={() => handleEdit(entry)}
                                        >
                                            Редагувати
                                        </Button>
                                    </Card.Body>
                                </Card>
                            ))}
                            </div>
                        )}
                    </div>

                    {/* Десктоп / планшет: таблиця */}
                    <div className="d-none d-md-block leads-editor__desktop">
                        <div className="leads-editor__table-scroll">
                            <Table striped bordered hover className="table leads-editor__desktop-table mb-0">
                                <thead>
                                    <tr>
                                        <th>Дата</th>
                                        <th>Ліди Instagram</th>
                                        <th>Тікети Instagram</th>
                                        <th>Ліди Telegram</th>
                                        <th>Тікети Telegram</th>
                                        <th>Менеджер (Директ)</th>
                                        <th>Дії</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr className="leads-editor-row align-middle table-group-divider">
                                        <td>
                                            <DatePicker
                                                selected={date}
                                                onChange={setDate}
                                                dateFormat="dd-MM-yyyy"
                                                className={controlClassDesktop}
                                                wrapperClassName="w-100"
                                                withPortal
                                                portalId="datepicker-portal-root"
                                            />
                                        </td>
                                        <td>
                                            <Form.Control
                                                type="number"
                                                className="w-100"
                                                value={formData.leadsInstagram}
                                                onChange={(e) => handleChange("leadsInstagram", e.target.value)}
                                            />
                                        </td>
                                        <td>
                                            <Form.Control
                                                type="number"
                                                className="w-100"
                                                value={formData.ticketsInstagram}
                                                onChange={(e) => handleChange("ticketsInstagram", e.target.value)}
                                            />
                                        </td>
                                        <td>
                                            <Form.Control
                                                type="number"
                                                className="w-100"
                                                value={formData.leadsTelegram}
                                                onChange={(e) => handleChange("leadsTelegram", e.target.value)}
                                            />
                                        </td>
                                        <td>
                                            <Form.Control
                                                type="number"
                                                className="w-100"
                                                value={formData.ticketsTelegram}
                                                onChange={(e) => handleChange("ticketsTelegram", e.target.value)}
                                            />
                                        </td>
                                        <td>{renderManagerSelect(false)}</td>
                                        <td>
                                            <Button onClick={handleSave}>
                                                Зберегти
                                            </Button>
                                        </td>
                                    </tr>
                                    {sortedList.map((entry) => (
                                        <tr key={entry.date}>
                                            <td>{formatDateForTable(entry.date)}</td>
                                            <td>{entry.leads?.instagram || 0}</td>
                                            <td>{entry.tickets?.instagram || 0}</td>
                                            <td>{entry.leads?.telegram || 0}</td>
                                            <td>{entry.tickets?.telegram || 0}</td>
                                            <td>{summaryDirectNames(entry).join(" + ") || "Не обрано"}</td>
                                            <td>
                                                <Button variant="warning" onClick={() => handleEdit(entry)}>
                                                    Редагувати
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
